<script lang="ts" setup>
    import { onMounted, ref, reactive, computed} from 'vue';
    import {IRemarkCodesList, IEobRemarkCodes, IAddRemarkCodeInputs, IDeleteRemarkCodeInput} from '../../lib/Models/RemarkCodesModel';
    import vSelect from 'vue-select'; 
    import * as _ from 'lodash';
    import { PageTitle } from '../../LibMain';
    import WindowHeaderFooter from '../../components/WindowHeaderFooter.vue';
    import ActionButtonV2 from '../../components/ActionButtonV2.vue'; 
    import { ActionButtonStyle } from '../../lib/Types/Types'; 
    import { confirmDialog, popAlert } from '../../lib/Notification';
    import { ButtonDisplaySize, IconTypes, DisplayStyle, HoverCursorTypes } from "../../Enums/UiEnums";
    import * as ChoiceApi from '../../lib/ChoiceApi';
    import { ICol } from "../../lib/ChoiceTableHelpers";
    import ChoiceTable from "../../components/ChoiceTable.vue";
    import Icon from '../../components/Icon.vue';

    import '../../../css/choice.css';
    import 'vue-select/dist/vue-select.css';

    const props = defineProps<{
        screenName: string,
        lookupRemarkCodes: IRemarkCodesList[],
        depositDetailId: number
      }>();

    const RemarkCodeTable = ref<InstanceType<typeof ChoiceTable>>();  

    const tableColumns = ref<ICol[]>([
        { Name: 'Id', DataField: 'remark_code_id' },
        { Name: 'Remark Code', DataField: 'remark_code' },
        { Name: 'Remark Code Description', DataField: 'remark_code_desc' }, 
        { Name: 'Actions', DataField: 'empty' }   
    ]);

    const remarkData = reactive<{        
        remarkCodeList?: IRemarkCodesList[],
        detailId2: number,
        remarkCodesById?: IEobRemarkCodes[],
        procedureCode?: string,
        selectedRemarkCodes: string[],
        addRemarkCodeInputs: IAddRemarkCodeInputs,
        deleteRemarkCodeInput: IDeleteRemarkCodeInput
    }>({  
        detailId2: props.depositDetailId,
        selectedRemarkCodes: [],
        addRemarkCodeInputs:
            {
                detail_id_2: props.depositDetailId,
                selectedRemarkCodes: []
            },
        deleteRemarkCodeInput:
            {
                remark_code_id: 0
            }
    });

    onMounted(async () => {     
        remarkData.remarkCodeList = props.lookupRemarkCodes;  
        
        remarkData.remarkCodeList.forEach(item => {
            item.remarkCodeAndDesc = `${item.remark_code} - ${item.description}`;
        });

        loadRemarkCodesById();
    });

    const hasNoRemarkCodes = computed(() => {
        return remarkData.remarkCodesById?.some(item => item.remark_code_id === null);
    }); 
    
    const procCode = computed(() => {
        return remarkData.remarkCodesById?.find(item => item.procedure_code !== null);
    }); 

    const windowHeight = computed(() => {
        return hasNoRemarkCodes ? 20 : 30;
    });

    async function loadRemarkCodesById() {
        const resp = await ChoiceApi.Claim.getEobRemarksById(remarkData.detailId2);

        if (!resp) {
            await popAlert("There was an error pulling the Remark Codes for this procedure", "Eob Remark Codes");
            return;
        }

        remarkData.remarkCodesById = resp;        
    }

    async function deleteRemark(row: any) {
        const msg = `Are you sure you want to delete this remark code (${row.remark_code})?`;
        const result = await confirmDialog(msg, { RejectText: "Cancel", ConfirmText: "Confirm" });                

        if (result) {    
            remarkData.deleteRemarkCodeInput.remark_code_id = row.remark_code_id;
            const resp = await ChoiceApi.Claim.deleteRemarkCode(remarkData.deleteRemarkCodeInput);

            if (!resp) {
                await popAlert("There was an error deleting the Remark Code for this procedure", "Eob Remark Codes");
                return;
            }

            //reload the Remark Codes
            await loadRemarkCodesById();
        }        
    }

    async function addRemark() {
        //check that remark code isn't already in the list
        const hasExistingRemarkCode = remarkData.selectedRemarkCodes.some(selectedCode => 
            remarkData.remarkCodesById?.some(existingRemark => existingRemark.remark_code === selectedCode)
        );

        if (hasExistingRemarkCode) {
            await popAlert("At least one remark code is already associated to this procedure.");
            return;
        }

        remarkData.addRemarkCodeInputs.detail_id_2 = remarkData.detailId2;
        remarkData.addRemarkCodeInputs.selectedRemarkCodes = remarkData.selectedRemarkCodes;
        const resp = await ChoiceApi.Claim.addRemarkCodes(remarkData.addRemarkCodeInputs);

        if (!resp) {
            await popAlert("There was an error adding the Remark Code(s) for this procedure", "Eob Remark Codes");
            return;
        }

        //clear out the drop down box
        remarkData.selectedRemarkCodes = [];

        //reload the Remark Codes
        await loadRemarkCodesById();

    }

    async function closeWindow() {
        parent.opener.window_close();
        window.close();
    }

</script>


<template>
    <PageTitle :security-screen-name="props.screenName" >
        <template #permitted>   
            <WindowHeaderFooter :headerHeight="3.5" :footerHeight="3.5" :workareaHeight="25">
                <template v-slot:WindowHeader>
                    <div class="row mt-2">
                        <div class="col col-md-6 isalus-page-title-large pt-1">EOB Remark Codes</div>                        
                    </div>
                </template>
                <template v-slot:WindowBody>  
                    <div class="row">
                        <div class="col ms-2">
                            <div class="row ps-1 fw-bold">
                                <div class="col text-end">
                                    Procedure Code:
                                </div>
                                <div class="col text-start">
                                    {{ procCode?.procedure_code }}
                                </div>
                            </div>
                            <div class="row ps-1 mt-3 mb-3">
                                <div class="col col-md-3 pe-1 text-end">
                                    Add a remark code:
                                </div>
                                <div class="col label-isalus-small">
                                    <v-select 
                                        v-model="remarkData.selectedRemarkCodes"
                                        :options="remarkData.remarkCodeList" 
                                        :reduce="item => item.remark_code" label="remarkCodeAndDesc" 
                                        :multiple="true" 
                                        :searchable="true"
                                        :filterable="true"
                                        :closeOnSelect="true"
                                        :select-on-tab="true" />
                                </div>
                                <div class="col col-md-2 pe-1 text-start">
                                    <ActionButtonV2 
                                        :disabled="remarkData.selectedRemarkCodes.length == 0"
                                        :action="ActionButtonStyle.Add"
                                        :text="'Add Remark'"
                                        :size="ButtonDisplaySize.Smaller" 
                                        :spinText="'Adding...'"
                                        :performAction="addRemark"
                                    />
                                </div>
                            </div>
                            <div class="row ps-1" v-if="hasNoRemarkCodes">
                                This procedure code does not currently have any remark codes associated with it.
                            </div>
                            <div class="row pt-1" v-if="!hasNoRemarkCodes"> 
                                <ChoiceTable 
                                    ref="RemarkCodeTable"
                                    :columns="tableColumns" 
                                    :data="remarkData.remarkCodesById" 
                                    :show-filters="false" 
                                    :allowSorting="true"
                                    fontSize="smaller"
                                    :key-value-data-member="'remark_code_id'"
                                >                                    
                                    <template #cell="{col, row, value}">                                 
                                        <template v-if="'Actions' === col.Name">
                                            <Icon :icon="IconTypes.Delete" :displayStyle="DisplayStyle.Danger" :hoverCursor="HoverCursorTypes.Pointer" title="Delete this remark code" @click="deleteRemark(row as any)"/>
                                        </template>
                                    </template>
                                </ChoiceTable>                  
                            </div>  
                        </div>                        
                    </div>   
                </template>
                <template v-slot:WindowFooter>
                    <div class="row">
                        <div class="col">
                            <span class="pe-2">
                                <ActionButtonV2 
                                    :action="ActionButtonStyle.Close"
                                    :text="'Close'"
                                    :size="ButtonDisplaySize.Small" 
                                    :spinText="'Closing...'"
                                    :performAction="closeWindow"
                                />
                            </span>        
                        </div> 
                    </div>
                </template> 
            </WindowHeaderFooter> 
        </template>
    </PageTitle>
</template>
